import React, { useEffect, useMemo, useRef, useState } from "react";

import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { Shadow } from "../../../../utils/BreakpointHelpers";
import { blue, red } from "../../../../utils/colors";
import { activeLink } from "../../activeLink";
import { IconPredefined } from "../../icons/Icon";
import { PredefinedIconType } from "../../icons/Icon/Predefined";
import ModalPortal from "../../Modal/Portal";
import Elements from "../";
import useSideNavStore from "./store";

const Container = styled.div`
    position: relative;
`;

const ElementWrapper = styled.div`
    ${({ theme }) => css`
        ${theme.breakpoints.upTo.small.css`
              margin-top: 65px;
        `}
        ${theme.breakpoints.upTo.extraSmall.css`
              margin-top: 70px;
        `}
    `}
    & > * {
        scroll-margin-top: 170px;
        @media not all and (min-resolution: 0.001dpcm) {
            @media {
                padding-top: 170px;
                margin-top: -170px;
            }
        }
        ${({ theme }) => css`
            ${theme.breakpoints.from.medium.css`
              scroll-margin-top: 80px;
              @media not all and (min-resolution: 0.001dpcm) {
                  @media {
                      padding-top: 80px;
                      margin-top: -80px;
                  }
              }
        `}
        `}
    }
`;

const SideNav = styled.div`
    pointer-events: none;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 49;
    bottom: 60px;
    overflow-y: scroll;

    ::-webkit-scrollbar {
        display: none;
    }

    -ms-overflow-style: none;
    scrollbar-width: none;
    margin-top: calc(8vh * 1.1);
    padding-top: 40px;
    width: fit-content;
    display: none;
    ${({ theme }) => theme.paddings.containerOuter.left}
    ${({ theme }) => css`
        ${theme.breakpoints.from.medium.css`
               display: block;
        `}
    `}
`;

const MobileSideNav = styled.div`
    ${Shadow};

    position: fixed;
    top: calc(8vh * 1);
    left: 0;
    z-index: 49;
    padding-top: 25px;
    padding-bottom: 15px;
    width: 100%;
    ${({ theme }) => theme.paddings.containerOuter.left}
    ${({ theme }) => theme.paddings.containerOuter.right}

    background-color: ${({ theme }) => theme.colors.white.toString()};
    color: ${({ theme }) => theme.colors.blue.toString()};
    ${({ theme }) => css`
        ${theme.breakpoints.from.small.css`
               padding-top: 25px;
        `}
    `}
    ${({ theme }) => css`
        ${theme.breakpoints.from.medium.css`
               display: none;
        `}
    `}
`;

const SideNavBackground = styled.div`
    ${(p) =>
        p.isScrolledToEnd
            ? css`
                  display: none;
              `
            : css`
                  display: block;
              `}

    ${({ theme }) => css`
        ${theme.breakpoints.from.medium.css`
               max-width: 220px;
        `}
    `}
`;

const HeadlineLink = styled.button`
    position: relative;
    cursor: pointer;
    font-weight: 500;
    font-size: 1.1em;
    width: fit-content;
    text-align: left;
    ${activeLink};

    padding: 0;

    &:hover {
        color: ${({ theme }) => theme.colors.red.toString()};
    }
    ${({ theme }) =>
        theme.breakpoints.from.medium.css`
            font-size: 1rem;
            line-height: 24px
    `}
    ${({ theme }) =>
        theme.breakpoints.from.large.css`
            font-size: 1rem;
    `}

    color: ${({ theme }) => theme.colors.blue.toString()};
    border-top: none;
    border-left: none;
    border-right: none;
    background-color: inherit;
`;

const Headline = styled.div`
    font-weight: 500;
    font-size: 1.1rem;
    padding-bottom: 5px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    ${({ theme }) => css`
        ${theme.breakpoints.from.medium.css`
           border: none;
        `}
    `};
`;

const LinkWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 75vh;
    padding-top: 15px;
    overflow-y: scroll;
    background-color: ${({ theme }) => theme.colors.white.toString()};
    ${(p) => !p.isOpen && p.theme.breakpoints.upTo.small.css`display: none;`}
    ${({ theme }) => css`
        ${theme.breakpoints.from.medium.css`
            overflow-y: unset;
            max-height: unset;
            gap: 10px;
            padding: 24px 24px 24px 24px;
            transform: translateX(-24px);
        `}
    `}
    a {
        border-color: ${({ theme }) => theme.colors.blue.toString()};
        border-bottom: solid 2px;
        ${({ theme }) => css`
            ${theme.breakpoints.from.medium.css`
                border: none;  
             `}
        `}
    }

    a:last-child {
        border-bottom: none;
        ${({ theme }) => css`
            ${theme.breakpoints.from.medium.css` 
                padding: 0; 
             `}
        `}
    }
`;

const StyledSmallShapeContainer = styled.div`
    pointer-events: all;
    ${({ theme }) => css`
        ${theme.breakpoints.from.extraLarge.css`
            transform: translateX(-110%);
        `}
    `}
`;

export const SideNavContainer = styled.div`
    .side-nav-element & {
        ${(p) => css`
            ${p.theme.breakpoints.from.medium.css`
               padding-left: ${p.paddingLeft ?? "240px"};
            `}
        `}
        ${({ theme }) => css`
            ${theme.breakpoints.from.extraLarge.css`
                padding-left: 0;
            `}
        `}
    }
`;

export const replaceMarkdownBreak = (value) => {
    return value.replace("-\\\n", "");
};

export const jumpToElement = (id) => {
    const element = document.getElementById(id);
    if (element) {
        element.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    }
};

const SideNavContainerElement = ({ navElements }) => {
    const sideNavBackgroundRef = useRef();
    const sideNavContainerElementWrapper = useRef();
    const [isOpen, setIsOpen] = useState(false);
    const [paddingLeft, setPaddingLeft] = useSideNavStore((state) => [
        state.paddingLeft,
        state.setPaddingLeft,
    ]);
    const scrollTimeoutRef = useRef(null);
    const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);

    const iconStyleOpen = css`
        transform: rotate(90deg);
    `;

    const iconStyleClosed = css``;

    const getHeadline = (element) => {
        switch (element.__typename) {
            case "ElementContentElement":
                if (element.name) {
                    return element.name;
                }
                if (element.items[0].name) {
                    return element.items[0].name;
                }
                break;
            case "ElementDoubleShape":
                if (element.items[0]) {
                    if (element.items[0].name) {
                        return replaceMarkdownBreak(element.items[0].name);
                    }
                }
                break;
            case "ElementThreeGoodReasons":
                if (element.name) {
                    return "3 " + element.name;
                }
                break;
            case "ElementShapeOverImg":
                if (element.name) {
                    return replaceMarkdownBreak(element.name);
                }
                break;
            case "ElementLngContractModels":
                if (element.headline) {
                    return element.headline;
                }
                break;
            case "ElementImgShape":
                if (element.text && element.headlineNumber !== "1") {
                    return replaceMarkdownBreak(element.text);
                }
                break;
            case "ElementPrebuiltContactForm":
                if (element.contactFormName === "FluessiggasZaehlerstand") {
                    return "Zählerstand eingeben";
                }
                break;
            case "ElementLngCalculator":
                if (element.name) {
                    return element.name;
                }
                break;
            case "ElementAdvertisingDoubleShape":
                if (element.headline) {
                    return element.headline;
                }
                break;
            case "ElementTextImgGrid":
                if (element.name) {
                    return element.name;
                }
                break;
            case "ElementCrossSelling":
                if (element.text) {
                    return replaceMarkdownBreak(element.text);
                }
                break;
            default:
                return false;
        }
    };

    const headlines = useMemo(() => {
        return navElements
            .filter((item) => getHeadline(item))
            .map((item) => getHeadline(item));
    }, [navElements]);

    const replaceForLink = (string) => {
        return string.toLowerCase().replaceAll(" ", "-");
    };

    const isPageScrolledToEnd = () => {
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        const scrollPosition =
            window.scrollY ||
            window.pageYOffset ||
            document.body.scrollTop +
                ((document.documentElement &&
                    document.documentElement.scrollTop) ||
                    0);
        setIsScrolledToEnd(windowHeight + scrollPosition >= documentHeight);
    };

    useEffect(() => {
        if (document && sideNavContainerElementWrapper) {
            let i = 0;
            for (const child of sideNavContainerElementWrapper.current
                .children) {
                const elementHeadline = getHeadline(navElements[i]);
                child.classList.add("side-nav-element");
                if (elementHeadline) {
                    child.id = replaceForLink(elementHeadline);
                }
                i++;
            }
        }
    }, []);

    const handleScrolled = () => {
        isPageScrolledToEnd();
        if (!sideNavBackgroundRef.current) return;
        const newPaddingLeft =
            sideNavBackgroundRef.current.getBoundingClientRect().width +
            15 +
            "px";
        if (paddingLeft !== newPaddingLeft) {
            setPaddingLeft(newPaddingLeft);
        }
        const sideNavStart =
            sideNavBackgroundRef.current.getBoundingClientRect().y;
        if (document) {
            let i = 0;
            for (const child of sideNavContainerElementWrapper.current
                .children) {
                const id = child.getAttribute("id");
                if (id) {
                    const childBoundingClientRect =
                        child.getBoundingClientRect();
                    const childStart = childBoundingClientRect.y;
                    const childEnd =
                        childStart + childBoundingClientRect.height;
                    const link = document.getElementById(`link-${id}`);

                    if (childStart < sideNavStart && childEnd > sideNavStart) {
                        link.classList.add("active");
                    } else {
                        link.classList.remove("active");
                    }
                }
                i++;
            }
        }
    };

    const onScroll = () => {
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }
        scrollTimeoutRef.current = setTimeout(handleScrolled, 250);
    };

    useEffect(() => {
        if (typeof window === "undefined") return;
        window.addEventListener("scroll", onScroll);
        onScroll();
        return () => {
            window.removeEventListener("scroll", onScroll);
        };
    }, [navElements]);

    return (
        <Container>
            <ModalPortal>
                <SideNav>
                    <SideNavBackground
                        id="sideNavContainer"
                        isScrolledToEnd={isScrolledToEnd}
                        ref={sideNavBackgroundRef}
                    >
                        <StyledSmallShapeContainer>
                            <LinkWrapper>
                                {headlines.map((item) => (
                                    <HeadlineLink
                                        key={item}
                                        id={`link-${replaceForLink(item)}`}
                                        onClick={() =>
                                            jumpToElement(replaceForLink(item))
                                        }
                                    >
                                        {item}
                                    </HeadlineLink>
                                ))}
                            </LinkWrapper>
                        </StyledSmallShapeContainer>
                    </SideNavBackground>
                </SideNav>
                <MobileSideNav>
                    <Headline onClick={() => setIsOpen(!isOpen)}>
                        Menü
                        <IconPredefined
                            css={isOpen ? iconStyleOpen : iconStyleClosed}
                            type={PredefinedIconType.chevron}
                            size="18px"
                            hoverColor={red}
                            color={blue}
                        />
                    </Headline>
                    <LinkWrapper isOpen={isOpen}>
                        {headlines.map((item) => (
                            <HeadlineLink
                                key={item}
                                id={`link-${replaceForLink(item)}`}
                                onClick={() => {
                                    setIsOpen(!isOpen);
                                    jumpToElement(replaceForLink(item));
                                }}
                            >
                                {item}
                            </HeadlineLink>
                        ))}
                    </LinkWrapper>
                </MobileSideNav>
            </ModalPortal>
            <ElementWrapper ref={sideNavContainerElementWrapper}>
                <Elements elements={navElements} />
            </ElementWrapper>
        </Container>
    );
};

export default SideNavContainerElement;
